import React, { useMemo } from 'react';

import * as styles from './styles';
import moment from 'moment';
import queryString from 'query-string';

import { type DateRange, Location, type PageableContent, type Sort, type SortOrder, type UUID, type Vehicle, type VehicleGroup } from '@cobliteam/common-types';

import { type Driver, isWebDataError, isWebDataSuccess, type VehicleRoute, type VehicleRouteFilters, type WebData } from '@cobliteam/api-lib';

import { Analytics } from '@cobliteam/analytics';

import { useVehicleGroup } from '@cobliteam/hooks';

import { CloseIcon, NoConnectionIcon, Placeholder, PlaceholderCarIcon, SingleDatePicker, TableLoading } from '@cobliteam/base-components';

import { Button, Divider, Page, Typography } from '@cobliteam/ui/components';

import { FiltersSection } from './components/FiltersSection/FiltersSection';
import { RoutesTable } from './components/RoutesTable/RoutesTable';

import { DEFAULT_TIME_SELECTION } from '../TimeFilter/TimeFilter';

export interface RoutesHistoryProps {
  vehicleRoutesPageWebData: WebData<PageableContent<VehicleRoute>>;

  vehiclesWebData: WebData<Vehicle[]>;
  driversById: Map<UUID, Driver>;
  vehiclesById: Map<UUID, Vehicle>;

  loadOnChanges: (period: DateRange, page: number, size: number, filters?: VehicleRouteFilters, sort?: Sort) => void;
  loadPageContent: () => void;
  onRequestAddress: (latitude: number, longitude: number) => void;
}

const DEFAULT_PERIOD: DateRange = {
  start: moment().startOf('day').subtract(1, 'day').valueOf(),
  end: moment().endOf('day').subtract(1, 'day').valueOf(),
};

export const RoutesHistory: React.FC<RoutesHistoryProps> = ({
  vehicleRoutesPageWebData,
  driversById,
  vehiclesWebData,
  vehiclesById,
  loadOnChanges,
  loadPageContent,
  onRequestAddress,
}) => {
  const [period, setPeriod] = React.useState(DEFAULT_PERIOD);
  const [isReminderOpen, setIsReminderOpen] = React.useState(true);

  const [wantedGroups, setWantedGroups] = React.useState<VehicleGroup[]>([]);
  const [wantedVehicleIds, setWantedVehicleIds] = React.useState<UUID[]>([]);
  const [wantedTimeSelection, setWantedTimeSelection] = React.useState<DateRange>(DEFAULT_TIME_SELECTION);
  const [sort, setSort] = React.useState<Sort | undefined>(undefined);

  const [currentPage, setCurrentPage] = React.useState(0);
  const [rowsPerPage, setRowsPerPage] = React.useState(25);

  const { getVehicleGroups } = useVehicleGroup();
  const { data: vehicleGroups, isSuccess: isVehicleGroupsSuccess, refetch: fetchVehicleGroups } = getVehicleGroups();

  const vehicleGroupsById = useMemo(() => {
    if (isVehicleGroupsSuccess) {
      return new Map(vehicleGroups.map((group) => [group.id, group]));
    }
    return new Map();
  }, [isVehicleGroupsSuccess]);

  React.useEffect(() => {
    Analytics.track('Tracking History Page Viewed');
    loadPageContent();
    fetchVehicleGroups();
  }, []);

  const condenseFilters = (): VehicleRouteFilters => {
    const wantedGroupIds = wantedGroups.length ? wantedGroups.map((_) => _.id) : undefined;
    const vehicleIds = wantedVehicleIds.length ? wantedVehicleIds : undefined;

    return { wantedGroups: wantedGroupIds, wantedVehicleIds: vehicleIds };
  };

  React.useEffect(() => {
    setCurrentPage(0);
    loadOnChanges(period, 0, rowsPerPage, condenseFilters(), sort);
  }, [period, rowsPerPage, wantedGroups, wantedVehicleIds, sort]);

  const handleReloadOnError = () => {
    Analytics.track('Tracking History Reloaded On Error');
    loadPageContent();
    loadOnChanges(period, currentPage, rowsPerPage, condenseFilters(), sort);
    fetchVehicleGroups();
  };

  const handleDayChange = (beginDayInMillis: number) => {
    const newPeriodEndMoment = moment(beginDayInMillis).endOf('day');
    const newPeriod = {
      start: beginDayInMillis,
      end: newPeriodEndMoment.valueOf(),
    };
    Analytics.track('Tracking History Day Selection Changed');
    setPeriod(newPeriod);
  };

  const handleReminderClose = () => {
    Analytics.track('Tracking History Reminder Closed');
    setIsReminderOpen(false);
  };

  const handleGroupFilterApply = (newVehicleGroups: VehicleGroup[]) => {
    Analytics.track('Tracking History Filtered By Vehicle Groups', {
      numberOfGroups: newVehicleGroups.length,
    });
    setWantedGroups(newVehicleGroups);
    setWantedVehicleIds([]);
  };

  const handleVehicleFilterApply = (newVehicleIds: UUID[]) => {
    Analytics.track('Tracking History Filtered By Vehicles', {
      numberOfVehicles: newVehicleIds.length,
    });
    setWantedVehicleIds(newVehicleIds);
  };

  const handleTimeFilterApply = (timeSelection: DateRange) => {
    const hoursRange = moment(timeSelection.end).diff(moment(timeSelection.start), 'hours');
    Analytics.track('Tracking History Filtered By Hour', { hoursRange });
    setWantedTimeSelection(timeSelection);
  };

  const handlePaginationChange = (newPage: number, newRowsPerPage: number) => {
    Analytics.track('Tracking History Pagination Changed', {
      newPage,
      newRowsPerPage,
    });
    setCurrentPage(newPage);
    setRowsPerPage(newRowsPerPage);
    loadOnChanges(period, newPage, newRowsPerPage, condenseFilters(), sort);
  };

  const handleRequestSort = (column: string, order: SortOrder) => {
    Analytics.track('Tracking History Table Sorted', { column, order });
    setSort({ column, order });
  };

  const handleClickHook = (vehicleId: UUID) => {
    Analytics.track('See Vehicle Tracking On Map From Tracking History Clicked');

    const params = queryString.stringify({
      vehicle_id: vehicleId,
      date: period.start,
    });
    const url = `${window.location.origin}${Location.TRACKING}?${params}`;
    window.open(url, '_blank')!.focus();
  };

  const toTableRow = (vehicleRoute: VehicleRoute, vehicle?: Vehicle) => {
    const { id } = vehicleRoute.vehicle;

    return {
      id,
      vehicleRoute,
      vehicleId: vehicle?.id || '',
      cobliId: vehicle?.cobliId,
      vehicle,
    };
  };

  const getTableRows = (vehicleRoutes: VehicleRoute[], vehicles: Vehicle[]) => {
    return vehicleRoutes.map((vehicleRoute) => {
      const currentVehicle = vehicles.find((vehicle) => vehicle.id === vehicleRoute.vehicle.id);

      return toTableRow(vehicleRoute, currentVehicle);
    });
  };

  const renderRoutesTimeline = () => {
    if (isWebDataError(vehicleRoutesPageWebData)) {
      return (
        <Placeholder className={styles.placeholderWithTextButton} text='Ops! Ocorreu um erro no sistema.' Icon={NoConnectionIcon}>
          <Button variant='text' onClick={handleReloadOnError}>
            Recarregar
          </Button>
        </Placeholder>
      );
    }
    if (isWebDataSuccess(vehicleRoutesPageWebData) && isWebDataSuccess(vehiclesWebData)) {
      const vehicleRoutesPage = vehicleRoutesPageWebData.data;
      const vehicles = vehiclesWebData.data;

      return (
        <>
          <div className={styles.filters}>
            <FiltersSection
              vehicleGroups={Array.from(vehicleGroupsById.values())}
              vehicles={Array.from(vehiclesById.values())}
              wantedGroups={wantedGroups}
              wantedVehicleIds={wantedVehicleIds}
              wantedTimeSelection={wantedTimeSelection}
              onVehicleFilterChanged={handleVehicleFilterApply}
              onVehicleGroupFilterChanged={handleGroupFilterApply}
              onTimeFilterChanged={handleTimeFilterApply}
            />
            <Divider />
          </div>

          {vehicleRoutesPage.empty ? (
            <Placeholder className={styles.placeholderWithoutTextButton} text='Não existem trajetos para os filtros utilizados' Icon={PlaceholderCarIcon} />
          ) : (
            <RoutesTable
              period={period}
              wantedTimeSelection={wantedTimeSelection}
              rows={getTableRows(vehicleRoutesPage.content, vehicles)}
              totalRowsLength={vehicleRoutesPage.totalElements}
              rowsPerPage={vehicleRoutesPage.size}
              currentPage={vehicleRoutesPage.number}
              sort={sort}
              driversById={driversById}
              onSeeOnMapClick={handleClickHook}
              onPaginationChange={handlePaginationChange}
              onRequestSort={handleRequestSort}
              onRequestAddress={onRequestAddress}
            />
          )}
        </>
      );
    }
    return <TableLoading />;
  };

  return (
    <Page>
      <Page.Header>
        <Typography variant='heading-1'>Trajetos</Typography>
      </Page.Header>

      <Page.Body>
        <div className='flex gap-2.5'>
          <div className='w-40'>
            <SingleDatePicker handleDayChange={handleDayChange} selectedDateInMillis={period.start} disabledDays={{ after: new Date() }} />
          </div>
          {isReminderOpen && (
            <div className={styles.reminder}>
              <b>Quer ver detalhes no mapa</b>? Você pode abrir um trajeto clicando no nome do veículo abaixo
              <CloseIcon onClick={handleReminderClose} />
            </div>
          )}
        </div>
        {renderRoutesTimeline()}
      </Page.Body>
    </Page>
  );
};
